import type { StytchError } from '@stytch/vanilla-js';
import { useSetAtom } from 'jotai';
import * as React from 'react';
import { useEffect, useState } from 'react';
import { KeyboardAvoidingView, Platform } from 'react-native';
import {
    BodySmallRegular,
    Button,
    ButtonSize,
    ButtonVariant,
    CheckboxWithLabel,
    FullScreenSpinner,
    H2Regular,
    isWeb,
    MailIcon,
    PillButton,
    SafeAreaView,
    SmartphoneIcon,
    XStack,
    YStack,
} from 'smoothie';

import BaseOtpPage from '../../../../test/pages/auth/OtpPage';
import { IS_PRODUCTION, IS_TEST } from '../../../config/FruitfulEnvironment';
import { useViewEvents } from '../../../events/hooks/useViewEvents';
import { captureException } from '../../../logger/Logger';
import { sendOtpCode } from '../../../user/api/UserApi';
import { useUser } from '../../../user/providers/UserProvider';
import { fetchDeviceToken } from '../../api/AuthApi';
import { SESSION_DURATION_MINUTES } from '../../constants';
import { storeDeviceToken } from '../../helpers';
import { useStytch } from '../../hooks/useAuth';
import { useRedirectToFinishSignup } from '../../hooks/useRedirectToFinishSignup';
import { isOtpSubmissionInProgressAtom } from '../../state/AuthAtoms';
import OTPInput from './OTPInput';

enum OTP_TYPE {
    SMS = 'sms',
    EMAIL = 'email',
}

interface OTPFormProps {
    isSmsEnabled: boolean;
    otpType: OTP_TYPE;
    toggleOTPType: () => void;
    onSuccess?: () => void;
    headerMessage?: string;
    includeRememberDevice?: boolean;
}

const OTP_EXPIRATION_MINUTES = 10;
const OTP_CODE_LENGTH = 6;

const OTPForm = ({
    isSmsEnabled,
    otpType,
    toggleOTPType,
    onSuccess,
    headerMessage,
    includeRememberDevice = isWeb,
}: OTPFormProps) => {
    const stytch = useStytch();
    const [methodId, setMethodId] = useState('');
    const [rememberDevice, setRememberDevice] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isSendingOtp, setIsSendingOtp] = useState(false);
    const [otpCode, setOtpCode] = useState('');
    const [authError, setAuthError] = useState<StytchError | null>(null);
    const [hasRetriedSendOTP, setHasRetriedSendOTP] = useState(false);

    const handleCodeChange = (value: string) => {
        setOtpCode(value);
        if (
            value.length === OTP_CODE_LENGTH &&
            !isSendingOtp &&
            !isSubmitting
        ) {
            void submitOTP(value);
        }
    };
    const setIsOtpSubmissionInProgressAtom = useSetAtom(
        isOtpSubmissionInProgressAtom,
    );

    useEffect(() => {
        if (!isSmsEnabled && otpType === OTP_TYPE.SMS) {
            captureException(
                new Error(
                    'User is unverified or has no phone number and trying to access SMS',
                ),
            );
            // user should not have access to SMS OTPs if they're not email verified. toggle them back to email.
            toggleOTPType();
        } else {
            void sendOTP(otpType);
        }
    }, [otpType, isSmsEnabled]);

    const sendEmailOTP = async (): Promise<string | null> => {
        const res = await sendOtpCode({
            authType: OTP_TYPE.EMAIL,
        });

        return res.emailResponse?.email_id ?? null;
    };

    const sendSMSOTP = async (): Promise<string | null> => {
        const res = await sendOtpCode({
            authType: OTP_TYPE.SMS,
        });

        return res.smsResponse?.phone_id ?? null;
    };

    const sendOTP = async (otpType: OTP_TYPE) => {
        setOtpCode('');
        setAuthError(null);
        setIsSendingOtp(true);

        const otpHandler = otpType === OTP_TYPE.SMS ? sendSMSOTP : sendEmailOTP;

        try {
            const method_id = await otpHandler();
            if (method_id == null) {
                captureException(
                    new Error('OtpHandler returned null method_id'),
                    { otpType },
                );
            }

            setMethodId(method_id ?? '');
            setIsSendingOtp(false);
        } catch (error) {
            // Only call 'captureException' here in Prod because exceptions here are acceptably common
            // in lower environments especially due to prolific duplicate phone number usage (which is an error here)
            if (IS_PRODUCTION) {
                captureException(new Error('Error sending OTP code to user'), {
                    message: (error as StytchError).message,
                });
            } else {
                console.log('Error sending OTP code to user', error);
            }
            setAuthError(error as StytchError);
            setIsSendingOtp(false);
            setMethodId('');

            // Since we hit an error, if we haven't already, then retry with the other OTP type
            if (!hasRetriedSendOTP) {
                toggleOTPType();
                setHasRetriedSendOTP(true);
            }
        }
    };

    const submitOTP = async (codeOverride?: string) => {
        const code = codeOverride ?? otpCode;
        if (methodId) {
            setIsSubmitting(true);
            setIsOtpSubmissionInProgressAtom(true);

            try {
                const response = await stytch.otps.authenticate(
                    code,
                    methodId,
                    {
                        session_duration_minutes: SESSION_DURATION_MINUTES,
                    },
                );

                if (response.status_code === 200) {
                    if (rememberDevice) {
                        const deviceToken = await fetchDeviceToken({
                            stytchId: response.user.user_id,
                            sessionId: response.session.session_id,
                            isFirstLogin: false,
                        });

                        if (deviceToken == null) {
                            captureException(
                                new Error('Unable to verify device with jwt'),
                            );
                        } else {
                            storeDeviceToken(deviceToken);
                        }
                    }

                    onSuccess?.();
                } else {
                    captureException(new Error('Login failed'), {
                        statusCode: response.status_code,
                    });
                }
            } catch (error) {
                setAuthError(error as StytchError);
            } finally {
                setIsSubmitting(false);
                setIsOtpSubmissionInProgressAtom(false);
            }
        } else {
            const error = new Error('No methodId set when submitting OTP');
            error.message = JSON.stringify({
                error_type: 'no_otp_method_id',
                description:
                    'This is likely the result of encountering an exception when trying to send the OTP code to the user',
            });
            captureException(error);
            setAuthError(error);
        }
    };

    if (isSubmitting) {
        return <FullScreenSpinner />;
    }

    return (
        <YStack
            alignItems="center"
            alignSelf="center"
            width="100%"
            flex={1}
            paddingHorizontal={16}
            marginTop={48}
            gap={55}
            maxWidth={450}
            $platform-web={{
                justifyContent: 'center',
            }}
            testID={BaseOtpPage.pageId}
        >
            <YStack maxWidth="90%" gap={12}>
                <H2Regular textAlign="center">
                    We sent you{' '}
                    {otpType === OTP_TYPE.SMS ? 'a text' : 'an email'}
                </H2Regular>
                {headerMessage != null ? (
                    <BodySmallRegular textAlign="center">
                        {headerMessage}
                    </BodySmallRegular>
                ) : (
                    <BodySmallRegular textAlign="center">
                        Enter the six-digit code to verify it&apos;s you. It
                        expires {OTP_EXPIRATION_MINUTES} minutes after you
                        request it.
                    </BodySmallRegular>
                )}
            </YStack>
            <YStack width="100%" alignItems="center" gap={8}>
                <OTPInput
                    code={otpCode}
                    setCode={handleCodeChange}
                    authError={authError}
                    clearAuthError={() => {
                        setAuthError(null);
                    }}
                    onSubmitEditing={submitOTP}
                />

                <YStack>
                    <XStack justifyContent="center" alignItems="center" gap={2}>
                        <BodySmallRegular color="$gray1000" marginLeft={8}>
                            Didn&apos;t receive a message?
                        </BodySmallRegular>
                        <PillButton
                            variant="tertiary"
                            text="Resend code"
                            onPress={() => {
                                void sendOTP(otpType);
                            }}
                            disabled={isSendingOtp || isSubmitting}
                        />
                    </XStack>

                    <XStack height={36} justifyContent="center">
                        {isSmsEnabled && ( // only show toggle if both email and sms are enabled
                            <PillButton
                                variant="tertiary"
                                text={`Use ${
                                    otpType === OTP_TYPE.SMS ? 'email' : 'phone'
                                } instead`}
                                icon={
                                    otpType === OTP_TYPE.SMS
                                        ? MailIcon
                                        : SmartphoneIcon
                                }
                                onPress={toggleOTPType}
                                disabled={isSendingOtp || isSubmitting}
                            />
                        )}
                    </XStack>
                </YStack>
            </YStack>
            <YStack width="100%" marginTop={isWeb ? 0 : 'auto'} gap={16}>
                {includeRememberDevice && (
                    <XStack alignItems="center" justifyContent="center">
                        <CheckboxWithLabel
                            id={BaseOtpPage.rememberDeviceCheckboxId}
                            label="Remember this device for 30 days"
                            checked={rememberDevice}
                            onPress={() => {
                                setRememberDevice((prev) => !prev);
                            }}
                        />
                    </XStack>
                )}
                <Button
                    disabled={
                        otpCode.length < 6 || isSendingOtp || isSubmitting
                    }
                    variant={ButtonVariant.PRIMARY}
                    size={ButtonSize.LARGE}
                    text="Submit"
                    onPress={() => {
                        void submitOTP();
                    }}
                    buttonProps={{
                        testID: BaseOtpPage.submitButtonId,
                    }}
                />
            </YStack>
        </YStack>
    );
};

export interface LoginOTPProps {
    overrideIsSmsEnabled?: boolean; // [ENG-2830] set to true to disallow sms regardless of user preferences
    onSuccess?: () => void;
    headerMessage?: string;
    includeRememberDevice?: boolean;
}

const LoginOTP = ({
    overrideIsSmsEnabled = false,
    onSuccess,
    headerMessage,
    includeRememberDevice = isWeb,
}: LoginOTPProps) => {
    const { trackViewOnce } = useViewEvents();
    trackViewOnce('LoginOTP');

    const [otpType, setOTPType] = useState<OTP_TYPE | null>(null);

    const { user, loading: isUserLoading } = useUser();
    const { needsToFinishSignup, redirectToFinishSignup } =
        useRedirectToFinishSignup();

    useEffect(() => {
        if (user == null && !isUserLoading && needsToFinishSignup) {
            redirectToFinishSignup();
        }
    }, [user, isUserLoading, needsToFinishSignup, redirectToFinishSignup]);

    useEffect(() => {
        // when we first fetch the user, we need to determine what the default OTP type should be,
        // then we can send the initial OTP code
        if (user) {
            if (user.disableMfa) {
                onSuccess?.();
                user.isFullyAuthed = true; // bypass OTP and trigger RedirectFullyAuthedUser
            } else if (otpType == null) {
                // Default to SMS OTP as long as...
                // email is verified, phone number is not null, and not in test env
                // Otherwise, default to email OTP
                const newOTPType =
                    user.isEmailVerified &&
                    user.phoneNumber != null &&
                    !overrideIsSmsEnabled &&
                    !IS_TEST
                        ? OTP_TYPE.SMS
                        : OTP_TYPE.EMAIL;
                setOTPType(newOTPType);
            }
        }
    }, [user]);

    const toggleOTPType = (): void => {
        const newType =
            otpType === OTP_TYPE.SMS ? OTP_TYPE.EMAIL : OTP_TYPE.SMS;
        setOTPType(newType);
    };

    if (user == null || isUserLoading || otpType == null) {
        return <FullScreenSpinner />;
    }

    return (
        <SafeAreaView style={{ flex: 1, width: '100%' }}>
            <KeyboardAvoidingView
                style={{
                    flex: 1,
                    width: '100%',
                }}
                behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
                keyboardVerticalOffset={16}
                ignoreIOSKeyboardWillChangeEvents={true}
            >
                <OTPForm
                    isSmsEnabled={
                        user.isEmailVerified &&
                        user.phoneNumber != null &&
                        !overrideIsSmsEnabled
                    }
                    otpType={otpType}
                    toggleOTPType={toggleOTPType}
                    onSuccess={onSuccess}
                    headerMessage={headerMessage}
                    includeRememberDevice={includeRememberDevice}
                />
            </KeyboardAvoidingView>
        </SafeAreaView>
    );
};

export default LoginOTP;
